import { useCallback, useMemo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Address, useFeeData, usePublicClient } from 'wagmi'
import { ethers } from 'ethers'

import { ContractWrite } from '../contract-write'
import { campaignPublicSelector, rewardPublicSelector, walletAddressSelector } from 'primsell-shared-layer/features'

import { RedeemABI } from 'primsell-shared-layer/core/contracts/abi/redeem'
import APP_CONFIG from 'primsell-shared-layer/core/config'

import { getGasPriceExtra } from '../lib'

import { amountToRedeemSelector, RedeemActions, redeemErrorSelector } from 'features'
import { RedeemStepsE } from 'types'

const redeemAddress = APP_CONFIG.contractRedeem.get(137) as `0x${string}`

const SubmitRedeem = () => {
  const isReward = window.location.pathname.includes('reward')
  const dispatch = useDispatch()

  const amountToRedeem = useSelector(amountToRedeemSelector)
  const campaignPublicDetails = useSelector(campaignPublicSelector)
  const rewardPublicDetails = useSelector(rewardPublicSelector)

  const contractAddress = isReward ? rewardPublicDetails?.contract?.address : campaignPublicDetails?.contractAddress
  const code = isReward ? rewardPublicDetails?.tokenId : campaignPublicDetails?.code
  const walletAddress = useSelector(walletAddressSelector) as `0x${string}`
  const redeemError = useSelector(redeemErrorSelector)

  const onError = useCallback(
    (error: string) => {
      dispatch(RedeemActions.setRedeemError(error))
    },
    [dispatch]
  )

  const onWriteSuccess = useCallback(
    (txHash: `0x${string}`) => {
      dispatch(RedeemActions.setTxHash(txHash))
      dispatch(RedeemActions.setCurrentStep(RedeemStepsE.WAIT_REDEEM))
    },
    [dispatch]
  )

  const feeData = useFeeData()
  const fetchFeeGasPrice = useMemo(() => {
    if (!feeData.isLoading && feeData.data?.formatted.gasPrice) {
      return getGasPriceExtra(feeData.data?.formatted.gasPrice)
    } else {
      return 0
    }
  }, [feeData.data?.formatted.gasPrice, feeData.isLoading])

  const publicClient = usePublicClient()
  const args = useMemo(() => [contractAddress, code, amountToRedeem], [contractAddress, code, amountToRedeem])

  const getEstimateGas = useCallback(async () => {
    const nonce = await publicClient.getTransactionCount({ address: walletAddress })
    const iface = new ethers.Interface(RedeemABI)
    const gasLimit = await publicClient.estimateGas({
      account: walletAddress,
      nonce,
      to: redeemAddress,
      data: iface.encodeFunctionData('redeem', args) as `0x${string}`,
    })

    return gasLimit
  }, [publicClient, walletAddress, args])

  const [gasLimit, setGasLimit] = useState<bigint>()
  useEffect(() => {
    async function fetchData() {
      const estimateGas = await getEstimateGas()
      setGasLimit(estimateGas)
    }
    void fetchData()
  }, [getEstimateGas])

  return (
    <>
      {gasLimit && fetchFeeGasPrice && !redeemError && (
        <ContractWrite
          address={redeemAddress as Address}
          abi={RedeemABI}
          functionName={'redeem'}
          from={walletAddress as Address}
          args={args}
          gasPrice={fetchFeeGasPrice}
          gasLimit={gasLimit}
          onError={onError}
          onWriteSuccess={onWriteSuccess}
        />
      )}
    </>
  )
}
export { SubmitRedeem }
